import React from 'react';
import { Box, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { tokens } from '../../../theme';

export interface DateRange {
  dateFrom: string;
  dateTo: string;
}

export type RangePreset = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

const PRESETS: { key: RangePreset; label: string; title: string }[] = [
  { key: 'day', label: 'Dziś', title: 'Tylko dzisiejszy dzień' },
  { key: 'week', label: '7 dni', title: 'Ostatnie 7 dni' },
  { key: 'month', label: 'Miesiąc', title: 'Ostatni miesiąc' },
  { key: 'quarter', label: 'Kwartał', title: 'Ostatnie 3 miesiące' },
  { key: 'year', label: 'Rok', title: 'Ostatnie 12 miesięcy' },
  { key: 'custom', label: 'Własny', title: 'Własny zakres dat' },
];

const iso = (d: Date) => d.toISOString().split('T')[0];

export function computePresetRange(preset: RangePreset): DateRange {
  const now = new Date();
  const to = iso(now);
  const from = new Date(now);
  switch (preset) {
    case 'day':
      break; // from = today
    case 'week':
      from.setDate(from.getDate() - 6);
      break;
    case 'quarter':
      from.setMonth(from.getMonth() - 3);
      break;
    case 'year':
      from.setFullYear(from.getFullYear() - 1);
      break;
    case 'month':
    default:
      from.setMonth(from.getMonth() - 1);
  }
  return { dateFrom: iso(from), dateTo: to };
}

interface Props {
  value: DateRange;
  preset: RangePreset;
  onChange: (range: DateRange, preset: RangePreset) => void;
}

const DATE_FIELD_SX = { width: { xs: '100%', sm: 180 } };

export const StatsDateRange: React.FC<Props> = ({ value, preset, onChange }) => {
  const selectPreset = (key: RangePreset) => {
    if (key === 'custom') {
      onChange(value, 'custom');
      return;
    }
    onChange(computePresetRange(key), key);
  };

  const setFrom = (dateFrom: string) => onChange({ ...value, dateFrom }, 'custom');
  const setTo = (dateTo: string) => onChange({ ...value, dateTo }, 'custom');

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      gap={{ xs: 2, md: 3 }}
      alignItems={{ xs: 'stretch', md: 'flex-end' }}
      justifyContent="space-between"
      sx={{ mb: 3 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography component="div" id="stats-range-label" sx={{ fontSize: '0.875rem', fontWeight: 600, color: tokens.ink, mb: 0.75, lineHeight: 1.4 }}>
          Okres
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={preset}
          onChange={(_e, v: RangePreset | null) => v && selectPreset(v)}
          aria-labelledby="stats-range-label"
          sx={{
            // Na telefonie przyciski ukladaja sie w siatke 3 x 2, zeby nic nie wystawalo poza ekran.
            display: { xs: 'grid', sm: 'inline-flex' },
            gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))' },
            gap: { xs: 0.75, sm: 0 },
            bgcolor: { xs: 'transparent', sm: tokens.paper },
            '& .MuiToggleButton-root': { minHeight: 40, px: { sm: 1.75 }, whiteSpace: 'nowrap' },
            '@media (max-width:599.95px)': {
              // !important: domyslne style grupy (.grouped:not(:first-of-type)) maja wyzsza specyficznosc.
              '& .MuiToggleButtonGroup-grouped': {
                borderRadius: '10px !important',
                border: `1px solid ${tokens.lineStrong} !important`,
                marginLeft: '0 !important',
              },
              '& .MuiToggleButtonGroup-grouped:not(.Mui-selected)': { backgroundColor: tokens.paper },
            },
          }}
        >
          {PRESETS.map((p) => (
            <ToggleButton key={p.key} value={p.key} title={p.title} aria-label={p.title}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'auto auto' },
          gap: 1.5,
          justifyContent: { sm: 'start', md: 'end' },
        }}
      >
        <TextField
          type="date"
          size="small"
          label="Od"
          value={value.dateFrom}
          onChange={(e) => setFrom(e.target.value)}
          inputProps={{ max: value.dateTo }}
          InputLabelProps={{ shrink: true }}
          sx={DATE_FIELD_SX}
        />
        <TextField
          type="date"
          size="small"
          label="Do"
          value={value.dateTo}
          onChange={(e) => setTo(e.target.value)}
          inputProps={{ min: value.dateFrom }}
          InputLabelProps={{ shrink: true }}
          sx={DATE_FIELD_SX}
        />
      </Box>
    </Stack>
  );
};
